import * as THREE from 'three';
import { BlockPlanet, BlockData } from './BlockPlanet';
import { PlanetGenerator } from './PlanetGenerator';

export class OrbitController {
  private planets: Map<number, BlockPlanet>;
  private maxPlanets: number;
  private planetGenerator: PlanetGenerator;
  private scene: THREE.Scene;
  private baseOrbitRadius: number;
  private orbitSpacing: number;
  private orbitSpeed: number;

  constructor(scene: THREE.Scene, maxPlanets: number = 10) {
    this.planets = new Map();
    this.maxPlanets = maxPlanets;
    this.planetGenerator = new PlanetGenerator();
    this.scene = scene;
    this.baseOrbitRadius = 30;
    this.orbitSpacing = 3;
    this.orbitSpeed = 0.1;
  }

  public addBlock(block: BlockData): BlockPlanet {
    // Remove oldest block if at capacity
    if (this.planets.size >= this.maxPlanets) {
      this.removeOldestBlock();
    }
    
    // Generate new planet
    const planet = this.planetGenerator.createPlanet(block);
    
    // Set userData for interaction system on both the group and the actual mesh
    // This ensures raycasting works regardless of which object is hit
    const userData = {
      id: `planet-${block.height}`,
      type: 'planet',
      interactive: true,
      blockData: block,
      blockHeight: block.height
    };
    
    // Set userData on the group (main mesh)
    planet.mesh.userData = userData;
    
    // CRITICAL FIX: Also set userData on the actual planet mesh for raycasting
    planet.planetMesh.userData = userData;
    
    // Assign orbit
    this.assignOrbit(planet);
    
    // Ensure planet is positioned immediately (critical for trajectory calculations)
    planet.update(0); // Force orbital position update
    
    // Add to scene
    this.scene.add(planet.mesh);
    
    // Store planet
    this.planets.set(block.height, planet);
    
    // Reorganize orbits for smooth transitions
    this.reorganizeOrbits();
    
    return planet;
  }

  private removeOldestBlock(): void {
    if (this.planets.size === 0) return;
    
    // Find oldest block (lowest height)
    let oldestHeight = Infinity;
    for (const height of this.planets.keys()) {
      if (height < oldestHeight) {
        oldestHeight = height;
      }
    }
    
    const planet = this.planets.get(oldestHeight);
    if (planet) {
      this.removePlanet(oldestHeight);
    }
  }

  public removePlanet(height: number): void {
    const planet = this.planets.get(height);
    if (!planet) return;
    
    // Remove from scene
    this.scene.remove(planet.mesh);
    
    // Dispose resources
    planet.dispose();
    
    // Remove from map
    this.planets.delete(height);
  }

  private assignOrbit(planet: BlockPlanet): void {
    const index = this.planets.size;
    
    // Calculate orbital parameters
    const radius = this.baseOrbitRadius + (index * this.orbitSpacing);
    const angle = (index * 36) * (Math.PI / 180); // Evenly distribute
    const speed = this.orbitSpeed / (1 + index * 0.1); // Slower for outer orbits
    
    planet.setOrbit(radius, angle, speed);
  }

  private reorganizeOrbits(): void {
    // Sort planets by block height
    const sortedPlanets = Array.from(this.planets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(entry => entry[1]);
    
    // Reassign orbits based on sorted order
    sortedPlanets.forEach((planet, index) => {
      const radius = this.baseOrbitRadius + (index * this.orbitSpacing);
      const currentAngle = planet.orbit.angle;
      const speed = this.orbitSpeed / (1 + index * 0.1);
      
      // Smoothly transition to new orbit
      planet.setOrbit(radius, currentAngle, speed);
      
      // Ensure planet is positioned immediately after orbit change
      planet.update(0); // Force orbital position update
    });
  }

  public updateOrbits(deltaTime: number): void {
    for (const planet of this.planets.values()) {
      planet.update(deltaTime);
    }
  }

  public getPlanetByHeight(height: number): BlockPlanet | undefined {
    return this.planets.get(height);
  }

  public getClosestPlanet(position: THREE.Vector3): BlockPlanet | null {
    let closestPlanet: BlockPlanet | null = null;
    let closestDistance = Infinity;
    
    for (const planet of this.planets.values()) {
      const distance = position.distanceTo(planet.getPosition());
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPlanet = planet;
      }
    }
    
    return closestPlanet;
  }

  public getAllPlanets(): BlockPlanet[] {
    return Array.from(this.planets.values());
  }

  public getPlanetCount(): number {
    return this.planets.size;
  }

  public setOrbitParameters(baseRadius: number, spacing: number, speed: number): void {
    this.baseOrbitRadius = baseRadius;
    this.orbitSpacing = spacing;
    this.orbitSpeed = speed;
    
    // Reorganize with new parameters
    this.reorganizeOrbits();
  }

  public highlightPlanet(height: number, highlight: boolean): void {
    const planet = this.planets.get(height);
    if (!planet) return;
    
    if (highlight) {
      planet.activity.surfaceGlow = 1.0;
      // Could add additional highlight effects
    } else {
      planet.activity.surfaceGlow = 0;
    }
  }

  public dispose(): void {
    // Dispose all planets
    for (const planet of this.planets.values()) {
      this.scene.remove(planet.mesh);
      planet.dispose();
    }
    
    this.planets.clear();
    this.planetGenerator.dispose();
  }
}